// Shared constants + small label/style helpers for the Logistics
// Carriers admin panel. Sibling to logisticsRoutesShared.ts -- same
// pattern (small `.ts` file, no JSX) so the panel + create form +
// list can all pull from one place without circular imports.
//
// Same input/button class strings as logisticsRoutesShared so the two
// logistics surfaces feel like one bucket; re-exported here rather
// than imported through routes-shared so a future "extract logistics
// commons" refactor has a single seam.
import type { LogiCarrier, LogiCarrierKind } from "@/lib/logistics";

export const inputClass =
  "w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100";
export const primaryBtnClass =
  "rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60";
export const ghostBtnClass =
  "rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-800 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-100 dark:hover:bg-zinc-700";

// Display labels for the 5 carrier_kind enum values defined in
// server/mfg-services/carriersService (VALID_CARRIER_KINDS) and the
// DB CHECK on logi_carriers.carrier_kind.
export const CARRIER_KIND_LABELS: Record<LogiCarrierKind, string> = {
  contracted: "Contracted",
  freelance: "Freelance",
  dealer_byo: "Dealer BYO",
  in_house: "In-house",
  other: "Other",
};

// Kind-driven chip Tailwind classes. Conservative palette:
//   contracted -- the "standard partner" -> indigo (matches primary btn family)
//   freelance  -- the "spot hire"         -> amber  (less committed)
//   dealer_byo -- the "customer's truck"  -> teal   (external to us)
//   in_house   -- the "our fleet"         -> emerald-700 (deepest commitment)
//   other      -- the catch-all           -> gray
export const CARRIER_KIND_CHIP_CLASSES: Record<LogiCarrierKind, string> = {
  contracted: "bg-indigo-600 text-white",
  freelance: "bg-amber-600 text-white",
  dealer_byo: "bg-teal-600 text-white",
  in_house: "bg-emerald-700 text-white",
  other: "bg-gray-500 text-white",
};

export const CARRIER_KINDS: readonly LogiCarrierKind[] = [
  "contracted",
  "freelance",
  "dealer_byo",
  "in_house",
  "other",
];

// Active/inactive filter values. "" = all (no filter applied);
// "true"/"false" map to the listLogisticsCarriers is_active boolean
// at request time -- kept as strings so the filter <select> values
// round-trip cleanly without separate boolean serialization.
export type CarriersActiveFilter = "" | "true" | "false";

export const ACTIVE_FILTER_LABELS: Record<CarriersActiveFilter, string> = {
  "": "All",
  true: "Active only",
  false: "Inactive only",
};

// "Expires soon" / "expired" classification for insurance + contract
// dates. Returned as a small struct so the list row can both color the
// label AND surface a tooltip-friendly status string.
export type ExpiryStatus = {
  kind: "none" | "ok" | "soon" | "expired";
  label: string;
};

const SOON_WINDOW_DAYS = 30;

export function classifyExpiry(iso: string | null): ExpiryStatus {
  if (!iso) return { kind: "none", label: "" };
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return { kind: "none", label: iso };
  const now = Date.now();
  const diffDays = Math.floor((t - now) / (1000 * 60 * 60 * 24));
  const display = new Date(iso).toLocaleDateString();
  if (diffDays < 0) return { kind: "expired", label: `${display} (expired ${-diffDays}d ago)` };
  if (diffDays <= SOON_WINDOW_DAYS) return { kind: "soon", label: `${display} (in ${diffDays}d)` };
  return { kind: "ok", label: display };
}

export function expiryClass(kind: ExpiryStatus["kind"]): string {
  switch (kind) {
    case "expired":
      return "text-red-700 dark:text-red-300 font-medium";
    case "soon":
      return "text-amber-700 dark:text-amber-300";
    default:
      return "text-gray-600 dark:text-zinc-400";
  }
}

export function formatCoverageAreas(areas: string[]): string {
  return areas.filter((a) => a && a.trim()).join(", ");
}

export function carrierNameSort(a: LogiCarrier, b: LogiCarrier): number {
  return a.name.localeCompare(b.name);
}
